import { AddressingType, ApartmentDTO, BuildingDTO, EntrancePlantDetailDTO, EntrancePlantStructureCreateDTO, EPDeviceTypeEnum, GateConfigDTO, GateDTO, RiserDTO } from "@/../client-generator/generated-clients/plants";
import { Options, Vue } from "vue-class-component";
import { mapState } from "vuex";
import { PropType } from "vue";
import ButtonSelection from "@/views/components/ButtonSelection/ButtonSelection.vue";
import { SET_APP_LOADING } from "@/core/store/action-types";
import plantsClient from "@/core/services/plants";
import { isNumber, isString } from '@microsoft/applicationinsights-core-js';
import { deepEquals } from "@/core/utils/utils";

@Options({
  name: "DialogAddEditGate",
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    gateToEdit: {
      type: Object as PropType<GateDTO>,
      required: false,
      default: null,
    },
    onlyDevices: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapState(['currentEntrancePlant']), 
  },
  components: { 
    ButtonSelection,
  },
  watch: {
    isVisible: function (newValue) {
      this.isDialogVisible = newValue;
      if (this.isDialogVisible) {
        this.initGate();
      }
    },
    "currentGate.gateName": function () {
      this.verifyValidityOfName()
    },
    "currentGate.scsAddress": function () {
      this.verifyValidityOfScsAddress()
    },
    "currentGate.newPeSelected": function (newVal) {
      if (newVal) {
        this.smartReaderDisabled = true;
        this.keypadDisabled = false;
      } else if (!this.currentGate.keypadSelected) {
        this.smartReaderDisabled = false;
      }
    },
    "currentGate.keypadSelected": function (newVal) {
      if (newVal) {
        this.smartReaderDisabled = true;
        this.newPeDisabled = false;
      } else if (!this.currentGate.newPeSelected) {
        this.smartReaderDisabled = false;
      }
    },
    "currentGate.smartReaderSelected": function (newVal) {
      if (newVal) {
        this.newPeDisabled = true;
        this.keypadDisabled = true;
      } else {
        this.newPeDisabled = false;
        this.keypadDisabled = false;
      }
    },
    "selectedBuilding": function() {
      if (!this.isEdit && this.currentEntrancePlant.hasRiserInterface)
      {
        this.buildingRisers = this.fillBuildingRiserDropdown();
        this.selectedBuildingIds = this.selectedBuilding ? [this.selectedBuilding.buildingId!] : [];
        this.currentGate.connectedBuildingIds = this.selectedBuilding ? [this.selectedBuilding.buildingId!] : [];
        this.currentGate.connectedRiserInterfaceId = this.buildingRisers[0]?.riserInterfaceId ?? null;
      }
    }
  },
  emits: ['hide-dialog', 'update-list'],
})
export default class DialogAddEditGate extends Vue {
  currentEntrancePlant!: EntrancePlantDetailDTO;
  gateToEdit!: GateDTO | null;
  isVisible!: boolean;
  onlyDevices!: boolean;
  isEdit = false;
  isDialogVisible = false;
  currentGate: GateConfigDTO = new GateConfigDTO();
  readonly MIN_SCS_ADDRESS = 0;
  readonly MAX_SCS_ADDRESS = 95;
  allBuildings = false;
  allRisers: {[buildingid: string]: boolean} = {};
  readonly AllBuildingsValue = "All_buildings";
  readonly AllRisersValue = "All_risers";
  newPeDisabled = false;
  keypadDisabled = false;
  smartReaderDisabled = false;
  selectAll: string[] = [];
  selectedBuildingIds: string[] = [];
  selectedRiserIds: {[buildingid: string]: string[]} = {};
  isValidName = true;
  invalidNameReason = '';
  isValidScs = true;
  invalidScsReason = '';
  
  //for riser interface plants
  selectedBuilding: BuildingDTO | null = null;
  buildingRisers: {name: string; riserInterfaceId: string, riserId: string}[] = [];

  get dialogTitle() {
    return this.isEdit
      ? this.$t('AppResourceEP.EditGate')
      : this.$t('AppResourceEP.SetupGateTitle');
  }

  get saveButtonDisabled() {
    let devicesSelected = this.currentGate.newPeSelected || this.currentGate.keypadSelected || this.currentGate.smartReaderSelected;
    let buildingsSelected = this.selectedBuildingIds.length > 0;
    let risersSelected = false;
    if (this.currentEntrancePlant.hasRiserInterface) {
      risersSelected = !!(this.currentGate.connectedRiserInterfaceId);
    } else {
      risersSelected = this.isBRFA ? this.buildings.every(b => this.selectedRiserIds[b.buildingId!] && this.selectedRiserIds[b.buildingId!].length > 0) : true;
    }
    return !devicesSelected || !buildingsSelected || !risersSelected || !this.isValidName || !this.isValidScs;
  }

  get isBRFA() { 
    return this.currentEntrancePlant.addressingType === AddressingType.BuildingRiserFloorApartment;
  }

  get buildings(): BuildingDTO[] {
    if (!this.currentEntrancePlant.buildings) return [];
    return this.currentEntrancePlant.buildings.filter((building) => this.selectedBuildingIds!.includes(building.buildingId!));
  }

  initGate() {
    if (this.gateToEdit) {
      this.isEdit = true;
      this.currentEntrancePlant.scsAddressHelper?.availableScsAddressGate?.push(this.gateToEdit.scsAddress!)
      this.currentGate = new GateConfigDTO(
        {
          scsAddress: this.gateToEdit.scsAddress,
          gateName: this.gateToEdit.name,
          newPeSelected: this.gateToEdit.devices?.some(
              (device) => device.type === EPDeviceTypeEnum.Linea5000
              ),
          keypadSelected: this.gateToEdit.devices?.some(
              (device) => device.type === EPDeviceTypeEnum.KeyPad
            ),
          smartReaderSelected: this.gateToEdit.devices?.some(
              (device) => device.type === EPDeviceTypeEnum.SmartAccess
              ),
          connectedBuildingIds: this.gateToEdit.connectedBuildingIds,
          connectedRiserIds: this.gateToEdit.connectedRiserIds,
          connectedApartmentsIds: this.gateToEdit.apartments,
          connectedRiserInterfaceId: this.gateToEdit.riserInterfaceId,
        }
      );
      if (this.currentEntrancePlant.hasRiserInterface) {
        const buildingId = this.currentGate.connectedBuildingIds ? this.currentGate.connectedBuildingIds[0] : null;
        this.selectedBuilding = this.currentEntrancePlant.buildings ? this.currentEntrancePlant.buildings.find(b => b.buildingId === buildingId) ?? null : null;
        this.buildingRisers = this.fillBuildingRiserDropdown();
      }
    } else {
      this.isEdit = false;
      this.currentGate = new GateConfigDTO(
        {
          scsAddress: this.currentEntrancePlant.scsAddressHelper?.firstAvailableScsAddressGate,
          gateName: `${this.$t('AppResourceEP.Gate')} ${this.currentEntrancePlant.gates!.length + 1}`,
          newPeSelected: false,
          keypadSelected: false,
          smartReaderSelected: false,
          // connectedBuildingIds: this.currentEntrancePlant.buildings?.map(b => b.buildingId!) ?? [],
          // connectedRiserIds: this.currentEntrancePlant.buildings?.flatMap(b => b.risers!.map(r => r.riserId!)) ?? [],
          connectedApartmentsIds: [],
        }
      );
      if (this.currentEntrancePlant.hasRiserInterface) {
        this.selectedBuilding = this.currentEntrancePlant.buildings ? this.currentEntrancePlant.buildings[0] : null;
      } else {
          this.currentGate.connectedBuildingIds = this.currentEntrancePlant.buildings?.map(b => b.buildingId!) ?? [];
          this.currentGate.connectedRiserIds = this.currentEntrancePlant.buildings?.flatMap(b => b.risers!.map(r => r.riserId!)) ?? [];
      }
    }
    if (!this.currentEntrancePlant.hasRiserInterface) {
      this.selectedBuildingIds = this.currentGate.connectedBuildingIds ?? [];
      if (this.selectedBuildingIds.length == this.currentEntrancePlant.buildings!.length) {
        this.allBuildings = true;
      }
      if (this.isBRFA) {
        this.currentEntrancePlant.buildings?.forEach(b => {
          this.selectedRiserIds[b.buildingId!] = this.currentGate.connectedRiserIds?.filter(r => b.risers?.some(riser => riser.riserId == r)) ?? [];
          if (this.selectedRiserIds[b.buildingId!]?.length == b.risers?.length) {
            this.allRisers[b.buildingId!] = true;
          }
        });
      }
    }
  }

  verifyValidityOfName() {
    if (this.currentGate.gateName === undefined) return;

    const nameToCheck = this.currentGate.gateName.trim();
    this.isValidName = !this.alreadyExist(nameToCheck);
    this.invalidNameReason = this.isValidName
      ? ''
      : this.$t('general.alreadyExisting');
  }

  verifyValidityOfScsAddress() {
    if (this.currentGate.scsAddress === undefined) return false;
    
    const value = Number(this.currentGate.scsAddress);

    const acceptedRange = value >= this.MIN_SCS_ADDRESS && value <= this.MAX_SCS_ADDRESS;
    const isItAvailable = this.currentEntrancePlant.scsAddressHelper?.availableScsAddressGate?.includes(value) ?? false;
    const doesNotExist = !this.alreadyExist(value)
    this.isValidScs = acceptedRange && doesNotExist && isItAvailable;
    if (!acceptedRange) {
      this.invalidScsReason = this.$t('AppResourceEP.GateScsAddressOutOfRange');
    } else if (!doesNotExist) {
      this.invalidScsReason = this.$t('AppResourceEP.ScsAddressAlreadyInUse');
    } else if (!isItAvailable) {
      this.invalidScsReason = this.$t('AppResourceEP.InputNotValid');
    } else {
      this.invalidScsReason = '';
    }
  }


  alreadyExist(value: string | number): boolean {
    let listToCheck = this.currentEntrancePlant.gates || [];
    if (this.isEdit) {
      listToCheck = listToCheck.filter(
        (gate) => gate.gateId !== this.gateToEdit?.gateId
      );
    }
    if (isString(value)) {
      const nameExist = listToCheck.some(
        (existing) => existing.name?.toLowerCase().trim() === (value as string).toLowerCase().trim()
      );
      return nameExist;
    } else if(isNumber(value)) {
      const addrExist = listToCheck.some(
        (existing) => existing.scsAddress === value as number 
      );
      return addrExist;
    }
    return true
  }

  retrieveApartmentIds() {
    if (this.isBRFA)
    {
      const selectBuildings = (this.currentEntrancePlant.buildings || [])
          .filter((b: BuildingDTO) => this.currentGate.connectedBuildingIds?.includes(b.buildingId!));
      const selectedApartments = selectBuildings
          .flatMap((b: BuildingDTO) => b.apartments || [])
          .filter((r: RiserDTO) => r.riserId && this.currentGate.connectedRiserIds?.includes(r.riserId))
          .map((a: ApartmentDTO) => a.apartmentId!);
      this.currentGate.connectedApartmentsIds = selectedApartments;
    } 
    else 
    {
      this.currentGate.connectedApartmentsIds = this.currentEntrancePlant.buildings
          ?.filter((b: BuildingDTO) => this.currentGate.connectedBuildingIds?.includes(b.buildingId!))
          .flatMap((b: BuildingDTO) => b.apartments || [])
          .map((a: ApartmentDTO) => a.apartmentId!) || [];
    }
  }

  async handleButtonSubmitAction() {
    this.isDialogVisible = false;
    await this.saveGate();

    this.$emit('update-list');
    this.$toast.add({
      severity: 'success',
      summary: this.$t('AppResourceEP.GateBannerAdded'),
      life: 3000,
    });
  }

  async saveGate() {
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      this.currentGate.connectedBuildingIds = this.selectedBuildingIds;
      if (this.currentEntrancePlant.hasRiserInterface) {
        var riserConnected = this.buildingRisers.find(r => r.riserInterfaceId === this.currentGate.connectedRiserInterfaceId);
        this.currentGate.connectedRiserIds = riserConnected && this.isBRFA ? [riserConnected.riserId] : [];
      }
      else {
        this.currentGate.connectedRiserIds = this.isBRFA ? Object.values(this.selectedRiserIds).flatMap(r => r) : [];
      }
      if(!deepEquals(this.currentGate.connectedBuildingIds, this.gateToEdit?.connectedBuildingIds) 
        || !deepEquals(this.currentGate.connectedRiserIds, this.gateToEdit?.connectedRiserIds)) {
        this.retrieveApartmentIds();
      }
      if (this.isEdit) {
        const gateToUpdate: {[key: string]: GateConfigDTO} = {[this.gateToEdit!.gateId!]: this.currentGate};
        await plantsClient.gatePUT(this.currentEntrancePlant.desktopId!, gateToUpdate);
      } else {
        await plantsClient.gatePOST(this.currentEntrancePlant.desktopId!, this.currentGate);
      }
    } catch (error) {
      console.log(error);
      setTimeout(() => {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          closable: false,
          life: 3000,
        });
      }, 1500);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  handleHideDialog() {
    this.isDialogVisible = false;
  }

  doHideDialog() {
    this.$emit('hide-dialog');
  }

  handleUpdateBuildingSelection(option: string, buildingId?: string, ) {
    if (option == this.AllBuildingsValue) {
      if (!this.allBuildings) {
        this.selectedBuildingIds.splice(0);
        this.currentEntrancePlant.buildings!.forEach(b => {
          this.selectedRiserIds[b.buildingId!] = [];
          this.allRisers[b.buildingId!] = false;
        });
      }
      else {
        this.currentEntrancePlant.buildings!.forEach(element => {
          if (!this.selectedBuildingIds.includes(element.buildingId!)) {
            this.selectedBuildingIds.push(element.buildingId!);
            this.selectedRiserIds[element.buildingId!] = [];
            this.allRisers[element.buildingId!] = false;
          }
        });
      }
    }
    else if(buildingId) {
      if (!this.allBuildings && this.selectedBuildingIds.length == this.currentEntrancePlant.buildings?.length) {
        this.allBuildings = true;
        this.selectedRiserIds[buildingId] = [];
        this.allRisers[buildingId] = false;
      }
      else if (this.allBuildings) {
        this.allBuildings = false;
        this.selectedRiserIds[buildingId] = [];
        this.allRisers[buildingId] = false;
       }
    }
  }

  handleUpdateRiserSelection(option: string, buildingId: string) {
    let building = this.currentEntrancePlant.buildings!.find(b => b.buildingId == buildingId);
    if (building) { 
      if (option == this.AllRisersValue) {
        if (!this.allRisers[buildingId]) {
          this.selectedRiserIds[buildingId].splice(0);
        }
        else {
          this.selectedRiserIds[buildingId] = building.risers!.map(r => r.riserId!);
        }
      }
      else {
        if (!this.allRisers[buildingId] && this.selectedRiserIds[buildingId].length == building.risers!.length) {
          this.allRisers[buildingId] = true;
        }
        else if (this.allRisers[buildingId]) {
          this.allRisers[buildingId] = false;
         }
       }
    }
  }

  //for riser interface plants
  fillBuildingRiserDropdown() {
    if (!this.selectedBuilding) return [];
    const result: {name: string; riserInterfaceId: string, riserId: string}[] = [];
    if (this.selectedBuilding.backboneInterface) {
      result.push({
        name: this.$t('AppResourceEP.Backbone') as string,
        riserInterfaceId: this.selectedBuilding.backboneInterface.riserInterfaceId ?? '',
        riserId: this.selectedBuilding.backboneInterface.riserId ?? ''
      });
    }
    if (this.selectedBuilding.risers) {
      for (const riser of this.selectedBuilding.risers) {
        result.push({
          name: riser.name ?? '',
          riserInterfaceId: riser?.interfaces?.[0]?.riserInterfaceId ?? '',
          riserId : riser.riserId ?? ''
        });
      }
    }
    return result;
  }

  getBuildingName(currentGate?: GateConfigDTO) {
    if (!currentGate?.connectedBuildingIds) return '';
    return this.selectedBuilding?.name ?? '';
  }

  getRiserName(currentGate?: GateConfigDTO) {
    if (!currentGate?.connectedRiserInterfaceId) return '';
    
    const riserInterfaceId = currentGate.connectedRiserInterfaceId;

    if (riserInterfaceId === this.selectedBuilding?.backboneInterface?.riserInterfaceId) {
      return this.$t('AppResourceEP.Backbone') as string;
    }

    const riser = this.buildingRisers.find(r => r.riserInterfaceId === riserInterfaceId);
    return riser?.name ?? '';
  }
}

